// ==============================================================================
// DASHBOARD ESPECÍFICO - ORGANIZADOR (IMPORTANDO TUS WIDGETS EXISTENTES)
// ==============================================================================

// src/components/dashboard/OrganizerDashboard.js
import React from 'react';
import { BaseDashboard } from './BaseDashboard';

// Imports de Material-UI (solo los necesarios para el widget de bienvenida)
import {
  Avatar,
  Box,
  Typography,
  Button,
  Grid
} from '@mui/material';

// Imports de iconos
import {
  Event as EventIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  CalendarToday as CalendarIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';

// =============================================================================
// IMPORTAR TODOS TUS WIDGETS EXISTENTES
// =============================================================================

// Importar widgets específicos del organizador desde sus ubicaciones originales
import CommunicationsWidget from '../organizer/CommunicationsWidget';
import EventCalendarWidget from '../organizer/EventCalendarWidget';
import EventManagementWidget from '../organizer/EventManagementWidget';
import OrganizerActivityWidget from '../organizer/OrganizerActivityWidget';
import OrganizerAnalyticsWidget from '../organizer/OrganizerAnalyticsWidget';
import OrganizerNotificationsWidget from '../organizer/OrganizerNotificationsWidget';
import OrganizerQuickActionsWidget from '../organizer/OrganizerQuickActionsWidget';
import OrganizerRevenueWidget from '../organizer/OrganizerRevenueWidget';
import OrganizerStatsWidget from '../organizer/OrganizerStatsWidget';
import ParticipantManagementWidget from '../organizer/ParticipantManagementWidget';

// =============================================================================
// WIDGET DE BIENVENIDA ESPECÍFICO (el único que creo aquí)
// =============================================================================

const OrganizerWelcomeWidget = ({ user }) => (
  <Box>
    <Box display="flex" alignItems="center" mb={3}>
      <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
        🎯
      </Avatar>
      <Box>
        <Typography variant="h5" gutterBottom>
          ¡Hola, {user?.displayName || 'Organizador'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Listos para crear eventos increíbles
        </Typography>
      </Box>
    </Box>
    
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Button 
          variant="contained" 
          fullWidth 
          size="small" 
          color="primary"
          startIcon={<EventIcon />}
        >
          Crear Evento
        </Button>
      </Grid>
      <Grid item xs={6}>
        <Button 
          variant="outlined" 
          fullWidth 
          size="small" 
          color="primary"
          startIcon={<PeopleIcon />}
        >
          Ver Todos
        </Button>
      </Grid>
    </Grid>
  </Box>
);

// =============================================================================
// COMPONENTE PRINCIPAL DEL DASHBOARD DEL ORGANIZADOR
// =============================================================================

export const OrganizerDashboard = ({ user, onWidgetsChange, dashboardData }) => {
  // Configuración de widgets usando tus componentes existentes
  const organizerWidgets = [
    { 
      id: 'org-welcome', 
      title: null, // Sin título para que se vea más limpio
      size: { xs: 12, md: 8 }, 
      component: OrganizerWelcomeWidget,
      order: 1,
      props: { user }
    },
    { 
      id: 'org-quick-actions', 
      title: 'Acciones Rápidas', 
      size: { xs: 12, md: 4 }, 
      component: OrganizerQuickActionsWidget,
      order: 2,
      props: {}
    },
    { 
      id: 'org-stats', 
      title: 'Mi Resumen', 
      size: { xs: 12, lg: 8 }, 
      component: OrganizerStatsWidget,
      order: 3,
      props: { data: dashboardData }
    },
    { 
      id: 'org-calendar', 
      title: 'Mi Calendario', 
      size: { xs: 12, lg: 4 }, 
      component: EventCalendarWidget,
      order: 4,
      props: { data: dashboardData?.events }
    },
    { 
      id: 'org-events', 
      title: 'Gestión de Eventos', 
      size: { xs: 12, md: 8 }, 
      component: EventManagementWidget,
      order: 5,
      props: { data: dashboardData?.events }
    },
    { 
      id: 'org-revenue', 
      title: 'Mis Ingresos', 
      size: { xs: 12, md: 4 }, 
      component: OrganizerRevenueWidget,
      order: 6,
      props: { data: dashboardData?.revenue }
    },
    { 
      id: 'org-participants', 
      title: 'Gestión de Participantes', 
      size: { xs: 12, md: 6 }, 
      component: ParticipantManagementWidget,
      order: 7,
      props: { data: dashboardData?.participants }
    },
    { 
      id: 'org-notifications', 
      title: 'Notificaciones', 
      size: { xs: 12, md: 6 }, 
      component: OrganizerNotificationsWidget,
      order: 8,
      props: {}
    },
    { 
      id: 'org-communications', 
      title: 'Comunicaciones', 
      size: { xs: 12, md: 6 }, 
      component: CommunicationsWidget,
      order: 9,
      props: { data: dashboardData?.communications }
    },
    { 
      id: 'org-activity', 
      title: 'Actividad Reciente', 
      size: { xs: 12, md: 6 }, 
      component: OrganizerActivityWidget,
      order: 10,
      props: { data: dashboardData?.recentActivity }
    },
    { 
      id: 'org-analytics', 
      title: 'Análisis de Rendimiento', 
      size: { xs: 12, lg: 8 }, 
      component: OrganizerAnalyticsWidget,
      order: 11,
      props: { 
        data: {
          eventSuccess: dashboardData?.successRate || 94,
          participantSatisfaction: 4.8,
          repeatParticipants: 65,
          averageParticipants: Math.round(dashboardData?.totalParticipants / dashboardData?.totalEvents) || 150,
          monthlyGrowth: dashboardData?.growth || 12
        }
      }
    }
  ];

  // Acciones personalizadas para el SpeedDial
  const customActions = [
    {
      icon: <EventIcon />,
      tooltip: "Crear Nuevo Evento",
      onClick: () => console.log('Abrir formulario de nuevo evento')
    },
    {
      icon: <NotificationsIcon />,
      tooltip: "Enviar Comunicado",
      onClick: () => console.log('Abrir centro de comunicaciones')
    },
    {
      icon: <CalendarIcon />,
      tooltip: "Ver Calendario",
      onClick: () => console.log('Abrir calendario completo')
    },
    {
      icon: <ReportIcon />,
      tooltip: "Generar Reporte",
      onClick: () => console.log('Abrir sección de reportes')
    }
  ];

  return (
    <BaseDashboard
      role="organizador"
      user={user}
      widgets={organizerWidgets}
      title="Panel de Organización"
      subtitle="Gestiona tus eventos y supervisa las inscripciones en tiempo real"
      roleIcon="🎯"
      roleColor="primary"
      onWidgetsChange={onWidgetsChange}
      customActions={customActions}
    />
  );
};

export default OrganizerDashboard;